"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdjudicationOutputIssueKind = void 0;
exports.validateAdjudicationOutput = validateAdjudicationOutput;
const consensus_types_1 = require("../../../../Consensus/consensus.types");
const adjudication_admitted_rows_1 = require("./adjudication-admitted-rows");
const adjudication_questions_1 = require("./adjudication-questions");
const adjudication_roster_1 = require("./adjudication-roster");
const adjudication_slice_1 = require("./adjudication-slice");
const adjudication_step_types_1 = require("./adjudication-step.types");
var AdjudicationOutputIssueKind;
(function (AdjudicationOutputIssueKind) {
    AdjudicationOutputIssueKind["UnanchoredDecision"] = "unanchored_decision";
    AdjudicationOutputIssueKind["DatumAnchorMismatch"] = "datum_anchor_mismatch";
    AdjudicationOutputIssueKind["InadmissibleValue"] = "inadmissible_value";
    AdjudicationOutputIssueKind["DuplicateDecision"] = "duplicate_decision";
    AdjudicationOutputIssueKind["MalformedSignalDecision"] = "malformed_signal_decision";
    AdjudicationOutputIssueKind["SignalNameCollision"] = "signal_name_collision";
    AdjudicationOutputIssueKind["DeletedSignalStillAssigned"] = "deleted_signal_still_assigned";
    AdjudicationOutputIssueKind["RowWithoutContent"] = "row_without_content";
})(AdjudicationOutputIssueKind || (exports.AdjudicationOutputIssueKind = AdjudicationOutputIssueKind = {}));
function validateAdjudicationOutput(input, output) {
    const questions = (0, adjudication_questions_1.buildAdjudicationQuestions)(input, output);
    const questionKeys = new Set(questions.map((question) => question.questionKey));
    const raisedAnchors = new Set(questions.map((question) => (0, consensus_types_1.anchorKey)(question.anchor)));
    return [
        ...groundTruthIssues(output.groundTruthDecisions, questionKeys, (0, adjudication_slice_1.adjudicationSliceOf)(input)),
        ...admittedRowIssues(input, output),
        ...errorIssues(output.errorDecisions, raisedAnchors),
        ...signalIssues(input, output),
    ];
}
function signalIssues(input, output) {
    const byId = new Map(input.roster.map((signal) => [signal.signalId, signal]));
    const pinned = (0, adjudication_roster_1.pinnedSignalNameKeysAfter)(input, output);
    const issues = [];
    const claimedBy = new Map();
    for (const decision of (0, adjudication_roster_1.effectiveSignalDecisions)(input, output)) {
        const questionKey = (0, adjudication_questions_1.signalQuestionKeyOf)(decision.name ?? '');
        const describe = decision.name ?? decision.signalId ?? '(unnamed)';
        const ruled = (0, adjudication_roster_1.isSignalRuled)(decision);
        if (!ruled) {
            issues.push({
                kind: AdjudicationOutputIssueKind.MalformedSignalDecision,
                questionKey,
                detail: `The '${decision.kind}' disposition for '${describe}' is missing what that kind needs.`,
            });
            continue;
        }
        if (decision.signalId !== undefined && !byId.has(decision.signalId)) {
            issues.push({
                kind: AdjudicationOutputIssueKind.MalformedSignalDecision,
                questionKey,
                detail: `Signal '${decision.signalId}' is not one this package holds.`,
            });
            continue;
        }
        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Delete) {
            issues.push(...deletionIssues(decision, byId, pinned, questionKey));
            continue;
        }
        const claimant = claimedBy.get(questionKey);
        if (claimant !== undefined) {
            issues.push(collisionIssue(claimant, decision, byId, questionKey));
            continue;
        }
        claimedBy.set(questionKey, decision);
    }
    return issues;
}
function collisionIssue(first, second, byId, questionKey) {
    const name = second.name ?? first.name ?? '';
    const renamedIds = new Set([first, second].flatMap((decision) => decision.kind === adjudication_step_types_1.SignalDispositionKind.Rename && decision.signalId !== undefined ? [decision.signalId] : []));
    if (renamedIds.size < 2) {
        return {
            kind: AdjudicationOutputIssueKind.DuplicateDecision,
            questionKey,
            detail: `The signal name '${name}' is disposed of more than once.`,
        };
    }
    const named = [...renamedIds].map((id) => byId.get(id)?.name ?? id);
    return {
        kind: AdjudicationOutputIssueKind.SignalNameCollision,
        questionKey,
        detail: `Signals '${named.join("' and '")}' would both become '${name}'. Merging two signals is not a ` +
            'ruling this review can make.',
    };
}
function deletionIssues(decision, byId, pinned, questionKey) {
    const signal = decision.signalId === undefined ? undefined : byId.get(decision.signalId);
    if (signal === undefined || !pinned.has((0, adjudication_roster_1.signalIdentityKey)(signal.name))) {
        return [];
    }
    return [
        {
            kind: AdjudicationOutputIssueKind.DeletedSignalStillAssigned,
            questionKey,
            detail: `Signal '${signal.name}' cannot be deleted while this review still assigns it to a pin.`,
        },
    ];
}
function groundTruthQuestionKey(anchor) {
    return anchor.kind === consensus_types_1.AnchorKind.Row ?
        (0, adjudication_questions_1.questionKeyOf)(adjudication_questions_1.AdjudicationQuestionKind.GroundTruthExistence, anchor)
        : (0, adjudication_questions_1.questionKeyOf)(adjudication_questions_1.AdjudicationQuestionKind.GroundTruthValue, anchor);
}
function groundTruthIssues(decisions, questionKeys, slice) {
    const issues = [];
    const seen = new Set();
    for (const decision of decisions) {
        const questionKey = groundTruthQuestionKey(decision.anchor);
        const anchor = decision.anchor;
        if (!questionKeys.has(questionKey)) {
            issues.push({
                kind: AdjudicationOutputIssueKind.UnanchoredDecision,
                anchor,
                questionKey,
                detail: `The review asks nothing about the ground truth at '${(0, consensus_types_1.anchorKey)(anchor)}'.`,
            });
            continue;
        }
        const mismatch = groundTruthDatumMismatch(decision);
        if (mismatch !== null) {
            issues.push({ kind: AdjudicationOutputIssueKind.DatumAnchorMismatch, anchor, questionKey, detail: mismatch });
        }
        if (anchor.kind === consensus_types_1.AnchorKind.Field &&
            decision.rawValue !== undefined &&
            !slice.admitsValue(anchor.column, decision.rawValue)) {
            issues.push({
                kind: AdjudicationOutputIssueKind.InadmissibleValue,
                anchor,
                questionKey,
                detail: `${JSON.stringify(decision.rawValue)} is not a value '${anchor.column}' can hold.`,
            });
        }
        if (seen.has(questionKey)) {
            issues.push({
                kind: AdjudicationOutputIssueKind.DuplicateDecision,
                anchor,
                questionKey,
                detail: `Ground truth at '${(0, consensus_types_1.anchorKey)(anchor)}' is ruled more than once.`,
            });
        }
        seen.add(questionKey);
    }
    return issues;
}
function admittedRowIssues(input, output) {
    return [...(0, adjudication_admitted_rows_1.admittedRowKeysOf)(input, output)].flatMap((rowKey) => {
        if ((0, adjudication_admitted_rows_1.admittedRowContentOf)(input, output, rowKey) !== null) {
            return [];
        }
        const anchor = { kind: consensus_types_1.AnchorKind.Row, rowKey };
        return [
            {
                kind: AdjudicationOutputIssueKind.RowWithoutContent,
                anchor,
                questionKey: groundTruthQuestionKey(anchor),
                detail: `Row '${rowKey}' is ruled present, but the package does not hold it and nothing records ` +
                    'what it contains, so it cannot be added. Rule it absent instead.',
            },
        ];
    });
}
function groundTruthDatumMismatch(decision) {
    if (decision.anchor.kind === consensus_types_1.AnchorKind.Row) {
        if (decision.existence === undefined) {
            return "A row ruling must state the row's existence.";
        }
        return decision.rawValue === undefined ? null : 'A row ruling cannot carry a cell value.';
    }
    if (decision.rawValue === undefined) {
        return "A cell ruling must state the cell's value.";
    }
    return decision.existence === undefined ? null : 'A cell ruling cannot carry a row existence.';
}
function errorIssues(decisions, raisedAnchors) {
    const issues = [];
    const seen = new Set();
    for (const decision of decisions) {
        const anchor = decision.anchor;
        const questionKey = (0, adjudication_questions_1.questionKeyOf)(adjudication_questions_1.AdjudicationQuestionKind.InferenceError, anchor, decision.rawValue);
        if (!raisedAnchors.has((0, consensus_types_1.anchorKey)(anchor))) {
            issues.push({
                kind: AdjudicationOutputIssueKind.UnanchoredDecision,
                anchor,
                questionKey,
                detail: `The review asks nothing about '${(0, consensus_types_1.anchorKey)(anchor)}'.`,
            });
            continue;
        }
        if (anchor.kind === consensus_types_1.AnchorKind.Row && decision.rawValue !== null) {
            issues.push({
                kind: AdjudicationOutputIssueKind.DatumAnchorMismatch,
                anchor,
                questionKey,
                detail: 'A row ruling cannot carry a cell value.',
            });
        }
        if (seen.has(questionKey)) {
            issues.push({
                kind: AdjudicationOutputIssueKind.DuplicateDecision,
                anchor,
                questionKey,
                detail: `The value at '${(0, consensus_types_1.anchorKey)(anchor)}' is ruled wrong more than once.`,
            });
        }
        seen.add(questionKey);
    }
    return issues;
}
//# sourceMappingURL=adjudication-validate.js.map